<script setup lang="ts">
import { computed } from 'vue';

import { formatDateStringWithTZ } from '@/utils/InputProcessingUtilities';
import HomePageCarousel from '@/components/carousels/HomePageCarousel.vue';
import useHomePageView from '@/composables/views/useHomePageView';
import { useUserStore } from '@/stores/useUserStore';

const userStore = useUserStore();
const userTimeZone = computed(() => userStore.user.timezone);

const {
  isFetchingData,
  serviceUpdates,
  serviceUpdatesLimit,
  newsLimited,
  limit,
  handleLimitClick,
  handleServiceUpdatesLimitClick,
} = useHomePageView();
</script>

<template>
  <div class="homePageView">
    <div class="wrapper pa-6 mb-12">
      <v-row>
        <v-col :cols="12">
          <HomePageCarousel class="mx-n6 mb-n2 mt-2" />
        </v-col>
      </v-row>
      <v-row>
        <!-- News Block -->
        <v-col :cols="6">
          <v-card class="mt-2" :loading="isFetchingData">
            <div class="card mt-2 mr-2 ml-3 pt-0" fluid>
              <v-row>
                <v-card-title class="card-header ma-0 pa-4">
                  News and Service updates
                </v-card-title>
                <v-spacer />
                <v-col
                  v-for="item in serviceUpdates"
                  :key="item.id"
                  cols="12"
                  class="pa-0 pr-2"
                >
                  <v-card flat>
                    <v-card-subtitle
                      v-if="item.created_at"
                      class="pt-4 pb-0 text-left"
                    >
                      {{
                        formatDateStringWithTZ(
                          item.created_at,
                          userTimeZone,
                          'yyyy-MM-dd',
                        )
                      }}
                    </v-card-subtitle>
                    <div class="">
                      <v-card-title>{{ item.title }}</v-card-title>
                      <v-card-text class="d-flex mt-4 flex-row pr-2">
                        <v-avatar v-if="item.image" size="110">
                          <v-img :src="item.image" />
                        </v-avatar>
                        <!-- eslint-disable vue/no-v-html -->
                        <div class="pt-4 pl-4" v-html="item.content"></div>
                        <!-- eslint-enable vue/no-v-html -->
                      </v-card-text>
                    </div>
                  </v-card>
                  <v-divider />
                </v-col>
              </v-row>
            </div>
            <v-card-actions v-if="serviceUpdatesLimit" class="justify-center">
              <v-btn
                block
                variant="text"
                @click="handleServiceUpdatesLimitClick(0)"
              >
                Show more
              </v-btn>
            </v-card-actions>
            <v-card-actions v-else class="justify-center">
              <v-btn
                block
                variant="text"
                @click="handleServiceUpdatesLimitClick(2)"
              >
                Show less
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
        <!-- WhatsNew Block -->
        <v-col :cols="6">
          <v-card class="mt-2" :loading="isFetchingData">
            <div class="card mt-2 mr-2 ml-3 pt-0" fluid>
              <v-row>
                <v-card-title class="card-header ma-0 pa-4">
                  What's new?
                </v-card-title>
                <v-spacer />
                <v-col
                  v-for="item in newsLimited"
                  :key="item.id"
                  cols="12"
                  class="pa-0 pr-2"
                >
                  <v-card flat>
                    <v-card-subtitle
                      v-if="item.created_at"
                      class="pt-4 pb-0 text-left"
                    >
                      {{
                        formatDateStringWithTZ(
                          item.created_at,
                          userTimeZone,
                          'yyyy-MM-dd',
                        )
                      }}
                    </v-card-subtitle>
                    <div class="">
                      <v-card-title>{{ item.title }}</v-card-title>
                      <v-card-text class="d-flex mt-4 flex-row pr-2">
                        <v-avatar v-if="item.image" size="110">
                          <v-img :src="item.image" />
                        </v-avatar>
                        <!-- eslint-disable vue/no-v-html -->
                        <div class="pt-4 pl-4" v-html="item.content"></div>
                        <!-- eslint-enable vue/no-v-html -->
                      </v-card-text>
                    </div>
                  </v-card>
                  <v-divider />
                </v-col>
              </v-row>
            </div>
            <v-card-actions v-if="limit" class="justify-center">
              <v-btn block variant="text" @click="handleLimitClick(0)">
                Show more
              </v-btn>
            </v-card-actions>
            <v-card-actions v-else class="justify-center">
              <v-btn block variant="text" @click="handleLimitClick(2)">
                Show less
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<!-- prettier-ignore -->
<style scoped lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.homePageView {
  .wrapper {
    max-width: 1440px;
  }

  .card {
    .card-header {
      width: 100%;
      border-top-left-radius: 6px;
      border-top-right-radius: 6px;
      background-color: colors.$color-brand-50;
    }
  }
}
</style>
